import { forwardRef } from 'react';
import mergeRefs from 'merge-refs';
import {
  useFormContext,
  type RegisterOptions,
} from '@leagueplatform/web-common';
import { useIntl } from '@leagueplatform/locales';
import {
  FormField,
  type GDSTextInputProps,
  TextInput,
} from '@leagueplatform/genesis-core';
import { useRequiredErrorMessage } from 'hooks/use-required-error-message/use-required-error-message.hook';

interface RHFFieldProps {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
}

export type MergeWithRHFField<T> = Omit<T, keyof RHFFieldProps> &
  RHFFieldProps & {
    registerOptions?: RegisterOptions;
    requiredMessage?: string;
  };

export const RHFTextInput = forwardRef<
  HTMLInputElement,
  MergeWithRHFField<GDSTextInputProps>
>(
  (
    {
      type,
      id,
      label,
      hint,
      required,
      requiredMessage,
      registerOptions,
      onChange,
      autoComplete,
      ...restComponentProps
    },
    ref,
  ) => {
    const { formatMessage } = useIntl();
    const {
      register,
      formState: { errors },
    } = useFormContext();

    const requiredErrorMessage = useRequiredErrorMessage(label);

    const { ref: RHFRegisterRef, ...restRHFRegisterProps } = register(id, {
      ...registerOptions,
      required: required && (requiredMessage || requiredErrorMessage),
      ...(onChange && { onChange }),
    });

    const errorMessage = errors?.[id]?.message;

    return (
      // FormField owns the label, hint, and status rendering, injects a
      // computed aria-describedby into the input, and announces the status
      // message from an aria-live region. `name`, `inputStatus`, and
      // `autoComplete` must live HERE, not on <TextInput>: FormField clones
      // its child with those props, so child-level values get overwritten —
      // an omitted `name` would clobber the RHF-registered name attribute.
      // FormField's `required` is display-only and defaults to a plain label
      // (no asterisk, no "(optional)" suffix), matching the Figma; the RHF
      // required *validation* is wired through register() above.
      <FormField
        id={id}
        name={id}
        label={label}
        hint={hint}
        autoComplete={autoComplete}
        inputStatus={errorMessage ? 'error' : undefined}
        statusIconLabel={formatMessage({ id: 'STR_ERROR' })}
        statusMessage={
          typeof errorMessage === 'string' ? errorMessage : undefined
        }
        css={{ width: '100%' }}
      >
        <TextInput
          type={type}
          {...restComponentProps}
          {...restRHFRegisterProps}
          ref={mergeRefs(RHFRegisterRef, ref)}
        />
      </FormField>
    );
  },
);

RHFTextInput.displayName = 'RHFTextInput';
